// typeflex ships no types of its own, so the declaration lives here in the
// package. A reference is the only way to carry an ambient `declare module`
// into every program that compiles this file — `@pdfking/imposition` imports
// this package, and its tsconfig sees nothing of ours.
// oxlint-disable-next-line typescript/triple-slash-reference
/// <reference path="../types/typeflex.d.ts" />
import type { Distance, Rectangle, SpatialRectangle } from "@pdfking/math"
import { ToSpatial } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { NullQ } from "@pdfking/structs"
import { FullMethodDispatcher } from "@pdfking/tfunc"
import { Node } from "typeflex"
import { YGEdge } from "typeflex/src/enums"

import type { Container, Page } from "../types/flex"
import { FlexEnum } from "../types/flex"
import type { Laid } from "../types/laid"
import type { Alloted } from "./allot"
import { allot } from "./allot"

// Convert our IR to typeflex nodes
const toFlexNode = FullMethodDispatcher<[Container<unknown> | Page<unknown>], Node>({
  [FlexEnum.Container]: item => {
    const node = Node.create()
    if (!NullQ(item.width)) node.setWidth(item.width)
    if (!NullQ(item.height)) node.setHeight(item.height)
    node.setFlexDirection(item.direction)
    node.setJustifyContent(item.justify)
    node.setAlignContent(item.align)
    node.setAlignItems(item.align)
    node.setPadding(YGEdge.Top, item.padding.top)
    node.setPadding(YGEdge.Right, item.padding.right)
    node.setPadding(YGEdge.Bottom, item.padding.bottom)
    node.setPadding(YGEdge.Left, item.padding.left)
    item.children.forEach((child, i) => node.insertChild(toFlexNode(child), i))
    return node
  },
  [FlexEnum.Page]: item => {
    const node = Node.create()
    node.setWidth(item.width)
    node.setHeight(item.height)
    return node
  },
})

// Read the computed layout back as one tree of absolute boxes.
const layoutTree = <D>(
  item: Container<D> | Page<D>,
  node: Node,
  offsetX: Distance,
  offsetY: Distance
): Laid<D> => {
  const layout = node.getComputedLayout()
  const x = (offsetX + layout.left) as Distance
  const y = (offsetY + layout.top) as Distance
  const children: Laid<D>[] = []
  if (item.enum === FlexEnum.Container) {
    for (let i = 0; i < node.getChildCount(); i++) {
      children.push(layoutTree(item.children[i], node.getChild(i), x, y))
    }
  }
  return {
    children,
    item,
    rect: { height: layout.height as Distance, width: layout.width as Distance, x, y },
  }
}

/**
 * Lay the tree out in `outer`, then share `sheet` among its pages.
 *
 * The two are not the same box: the tree keeps inside what the fill may use,
 * while the bleed reaches on into the margin around it, which is what the
 * outermost pages print into. `sheet` is in the tree's own coordinates, so a
 * box that starts before the origin is the margin on that side.
 */
export const computeLayout = <D>(
  root: Container<D>,
  outer: Rectangle<Distance>,
  sheet: SpatialRectangle<Distance> = ToSpatial(outer)
): Alloted<D> => {
  const rootNode = toFlexNode(root)
  rootNode.calculateLayout(outer.width, outer.height)

  const alloted = allot(layoutTree(root, rootNode, ZERO_DISTANCE, ZERO_DISTANCE), sheet)

  rootNode.freeRecursive()
  return alloted
}
